import { IFormValidationError, IProFormProps, IProFormStepsDialogOptions, IValidationError, ProForm } from "@ngt/opms";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useValidate from "../../../hooks/form/useValidate";
import FormTranslationProviderV2 from "../../providers/form/FormTranslationProviderV2";
import FormValidationTranslationProvider from "../../providers/FormValidationTranslationProvider";

interface ITranslatedProFormStepsDialogOptions extends Omit<IProFormStepsDialogOptions, 'skipText' | 'cancelText'> {
    skipButtonTextKey?: string,
    cancelButtonTextKey?: string,
}

interface ITranslatedProFormProps extends Omit<IProFormProps, 'cancelButtonText' | 'submitButtonText' | 'stepsText' | 'DialogOptions'> {
    formTranslationKey: string,
    cancelButtonTextKey?: string,
    submitButtonTextKey?: string,
    submitAndReturnButtonTextKey?: string,
    nextButtonTextKey?: string,
    okButtonTextKey?: string,
    backButtonTextKey?: string,
    DialogOptions?: ITranslatedProFormStepsDialogOptions
}

export const TranslatedProForm: FC<ITranslatedProFormProps> = ({
    formTranslationKey,
    children,
    cancelButtonTextKey,
    submitButtonTextKey,
    submitAndReturnButtonTextKey,
    nextButtonTextKey,
    okButtonTextKey,
    backButtonTextKey,
    DialogOptions,
    ...rest
}) => {
    const validate = useValidate();

    const { t } = useTranslation();

    const cancelButtonTextKeyToUse = useMemo(() => cancelButtonTextKey ?? "cancelButton", [cancelButtonTextKey]);
    const submitButtonTextKeyToUse = useMemo(() => submitButtonTextKey ?? "submitButton", [submitButtonTextKey]);
    const submitAndReturnButtonTextKeyToUse = useMemo(() => submitAndReturnButtonTextKey ?? "submitAndReturnButton", [submitAndReturnButtonTextKey]);
    const nextButtonTextKeyToUse = useMemo(() => nextButtonTextKey ?? "nextButton", [nextButtonTextKey]);
    const okButtonTextKeyToUse = useMemo(() => okButtonTextKey ?? "okButton", [okButtonTextKey]);
    const backButtonTextKeyToUse = useMemo(() => backButtonTextKey ?? "backButton", [backButtonTextKey]);
    const dialogSkipButtonTextKeyToUse = useMemo(() => `steps.dialog.${DialogOptions?.skipButtonTextKey ?? "skipButton"}`, [DialogOptions?.skipButtonTextKey]);
    const dialogCancelButtonTextKeyToUse = useMemo(() => `steps.dialog.${DialogOptions?.cancelButtonTextKey ?? "cancelButton"}`, [DialogOptions?.cancelButtonTextKey]);

    const cancelButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${cancelButtonTextKeyToUse}`, { defaultValue: "Cancel" }), [formTranslationKey, cancelButtonTextKeyToUse, t]);
    const submitButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${submitButtonTextKeyToUse}`, { defaultValue: "Submit" }), [formTranslationKey, submitButtonTextKeyToUse, t]);
    const submitAndReturnButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${submitAndReturnButtonTextKeyToUse}`, { defaultValue: "Submit and Return" }), [formTranslationKey, submitAndReturnButtonTextKeyToUse, t]);
    const nextButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${nextButtonTextKeyToUse}`, { defaultValue: "Next" }), [formTranslationKey, nextButtonTextKeyToUse, t]);
    const okButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${okButtonTextKeyToUse}`, { defaultValue: "Ok" }), [formTranslationKey, okButtonTextKeyToUse, t]);
    const backButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${backButtonTextKeyToUse}`, { defaultValue: "Back" }), [formTranslationKey, backButtonTextKeyToUse, t]);
    const dialogSkipButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${dialogSkipButtonTextKeyToUse}`, { defaultValue: "Skip" }), [formTranslationKey, dialogSkipButtonTextKeyToUse, t]);
    const dialogCancelButtonTextToUse = useMemo(() => t(`${formTranslationKey}.${dialogCancelButtonTextKeyToUse}`, { defaultValue: "Ok" }), [formTranslationKey, dialogCancelButtonTextKeyToUse, t]);

    const stepsTextToUse = useMemo(() => ({
        next: nextButtonTextToUse,
        cancel: cancelButtonTextToUse,
        back: backButtonTextToUse,
        ok: okButtonTextToUse,
        submit: submitButtonTextToUse
    }), [nextButtonTextToUse, cancelButtonTextToUse, backButtonTextToUse, okButtonTextToUse, submitButtonTextToUse])

    const updatedDialogOptions: IProFormStepsDialogOptions = useMemo(() => ({
        ...DialogOptions,
        skipText: dialogSkipButtonTextToUse,
        cancelText: dialogCancelButtonTextToUse
    }), [DialogOptions, dialogSkipButtonTextToUse, dialogCancelButtonTextToUse]);

    const getErrors = useCallback((errors: Record<string, IFormValidationError[]>) => {
        return Object.keys(errors).reduce<Record<string, IValidationError[]>>((current, propertyPath) => {
            current[propertyPath] = errors[propertyPath].map(error => {
                const simple = t(`${formTranslationKey}.error.${error.code}.simple`, { defaultValue: error.message });
                const detailed = error.message === error.detailedMessage ? simple : t(`${formTranslationKey}.error.${error.code}.detailed`, { defaultValue: error.detailedMessage });

                return {
                    ...error,
                    message: simple,
                    detailedMessage: detailed
                }
            });

            return current;
        }, {});
    }, [t, formTranslationKey]);

    const onValidate = useCallback(async (formState: any, formActions: any) => {
        const errors = await validate(formState, formActions);
        return getErrors(errors) as Record<string, IFormValidationError[]>;
    }, [validate, getErrors]);

    return (
        <FormTranslationProviderV2
            formTranslationKey={formTranslationKey}
        >
            <ProForm
                {...rest}
                DialogOptions={updatedDialogOptions}
                cancelButtonText={cancelButtonTextToUse}
                submitButtonText={submitButtonTextToUse}
                submitAndReturnButtonText={submitAndReturnButtonTextToUse}
                onValidate={onValidate}
                WrappingComponents={{
                    Form: FormValidationTranslationProvider,
                }}
                stepsText={stepsTextToUse}
            >
                {children}
            </ProForm>
        </FormTranslationProviderV2>
    )

}